// 剣舞のアルレ版の拍手 (2026-10-25・開発版 ?bladeArle=1)。
//   作者 (答え blade-100 q5): 「パリィ成功割合が8割を超えている場合、idleや歩行の前に拍手をする（攻撃判定なし）」
//   ・直近 10 回の受け (弾いた／食らった・防いだ) を数え、6 回以上あって弾いた割合が 8 割を超えたら、
//     連を終えて待機へ戻る所で 1.2 秒拍手する (その間は攻めも歩きもしない)。
//   ・続けては拍手しない (拍手した次の連の後はしない)。
import type { Boss, SfxId } from './types'
import { bladeSelfClapSfx } from './blade_sfx_v2'

const BOSS_ID = 'blade-dancer'
export const CLAP_WINDOW = 10
export const CLAP_MIN_SAMPLES = 6
export const CLAP_RATE = 0.8
export const CLAP_MS = 1200
const CLAP_FRAME_MS = 200
const CLAP_OPEN = 'm1025_k_1'    // 手を開いた
const CLAP_SHUT = 'm1025_k_2'    // 手を打ち合わせた
//   10-29 作者 (b3)「剣舞が拍手をしてください。各種のモーションも作る必要があると思います」:
//   入りと終わりに手を開いた絵で少し止め (立ち姿から急に打たない)、間で開く → 打つ を繰り返す。
//   入りの最初と終わりの最後は、手を胸へ持ち上げる途中の絵 (10-29 Codex 描き・m1029_clap_in) を挟む。
const CLAP_IN_MS = 240
const CLAP_OUT_MS = 240
const CLAP_LIFT_MS = 120
export const CLAP_LIFT = 'm1029_clap_in'
export const BLADE_CLAP_KEYS = [CLAP_LIFT, CLAP_OPEN, CLAP_SHUT] as const

let enabled = false
const recent: boolean[] = []
let clappedLast = false
let lastAttackCount = 0

export function setBladeClapEnabled(on: boolean): void { enabled = on; resetBladeClap() }
export function bladeClapEnabled(): boolean { return enabled }
export function resetBladeClap(): void { recent.length = 0; clappedLast = false; lastAttackCount = 0 }

/** この tick の音から受けの結果を足す ('parry' = 弾いた・'hit_player' / 'guard_player' = 弾けなかった)。 */
export function recordBladeClapResults(sfx: readonly string[]): void {
  for (const id of sfx) {
    if (id === 'parry') recent.push(true)
    else if (id === 'hit_player' || id === 'guard_player') recent.push(false)
  }
  if (recent.length > CLAP_WINDOW) recent.splice(0, recent.length - CLAP_WINDOW)
}

export function bladeParryRate(): number {
  return recent.length === 0 ? 0 : recent.filter(Boolean).length / recent.length
}

/** 前の tick と今の tick のボスを見て、連を終えて待機へ戻った所なら拍手を入れる。 */
export function applyBladeClap(prev: Boss, next: Boss, sfx: readonly string[]): Boss {
  if (!enabled || next.id !== BOSS_ID) return next
  if (next.attackCount < lastAttackCount) resetBladeClap()   // やり直し・新しい一戦
  lastAttackCount = next.attackCount
  recordBladeClapResults(sfx)
  if (prev.state !== 'recovery' || next.state !== 'idle' || next.hp <= 0) return next
  const clap = !clappedLast && recent.length >= CLAP_MIN_SAMPLES && bladeParryRate() > CLAP_RATE
  clappedLast = clap
  if (!clap) return next
  // 拍手の音: 既定は会話場面の拍手の借り物 (仮・10-29)・DEV ?bladeSfx=v2 は2拍の拍手 (src/blade_sfx_v2.ts)。配列は呼び元 (game.ts) の音の列。
  if (Array.isArray(sfx)) (sfx as SfxId[]).push(bladeSelfClapSfx())
  return { ...next, clapRemainingMs: CLAP_MS }
}

/** 待機の case で拍手の間を進める。拍手中なら true (攻めも歩きもしない)。 */
export function tickBladeClap(boss: Boss, dtMs: number): Boss | null {
  const left = boss.clapRemainingMs ?? 0
  if (left <= 0) return null
  return { ...boss, clapRemainingMs: Math.max(0, left - dtMs) }
}

/** 拍手中の絵 (入りと終わりは開いた手・間は 開く → 打つ を 200ms ずつ)。拍手していなければ null。 */
export function bladeClapKey(boss: Boss): string | null {
  const left = boss.clapRemainingMs ?? 0
  if (boss.state !== 'idle' || left <= 0) return null
  const t = CLAP_MS - left
  if (t < CLAP_LIFT_MS || left <= CLAP_LIFT_MS) return CLAP_LIFT
  if (t < CLAP_IN_MS || left <= CLAP_OUT_MS) return CLAP_OPEN
  return Math.floor((t - CLAP_IN_MS) / CLAP_FRAME_MS) % 2 === 0 ? CLAP_SHUT : CLAP_OPEN
}
